package com.havn.app.features.clients.data

import com.havn.app.core.logging.HavnLogger
import com.havn.app.core.media.HavnMediaBucket
import com.havn.app.core.media.HavnPickedPhoto
import com.havn.app.core.media.decodeHavnDataUrlBytes
import com.havn.app.core.media.havnDataUrlContentType
import com.havn.app.core.media.havnDisplayPhotoSource
import com.havn.app.core.media.havnPreviewStorageSource
import com.havn.app.core.media.havnPublicStorageUrl
import com.havn.app.core.media.havnPublicStorageSource
import com.havn.app.core.qr.HavnQrShareFiles
import com.havn.app.core.supabase.HavnSupabaseConfig
import com.havn.app.features.clients.domain.Client
import com.havn.app.features.clients.domain.ClientDraft
import com.havn.app.features.clients.domain.ClientInquiry
import com.havn.app.features.clients.domain.ClientOpening
import com.havn.app.features.clients.domain.ClientQrDeliveryAssets
import com.havn.app.features.clients.domain.ClientRepository
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.auth.auth
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.postgrest
import io.github.jan.supabase.postgrest.rpc
import io.github.jan.supabase.postgrest.query.Columns
import io.github.jan.supabase.postgrest.query.Order
import io.github.jan.supabase.storage.storage
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlin.io.encoding.Base64
import kotlin.io.encoding.ExperimentalEncodingApi
import kotlin.time.Clock

class SupabaseClientRepository(
    private val client: SupabaseClient,
    private val httpClient: HttpClient = HttpClient(),
    private val json: Json = Json { ignoreUnknownKeys = true }
) : ClientRepository {
    private val pageCacheMutex = Mutex()
    private val pageCache = mutableMapOf<SearchPageCacheKey, ClientPageCacheEntry>()

    override suspend fun clientsForAgent(agentId: String, agencyId: String?): Result<List<Client>> {
        return clientPage(agentId, agencyId, query = "", offset = 0, limit = CLIENT_PAGE_SIZE)
    }

    override suspend fun clientPage(
        agentId: String,
        agencyId: String?,
        query: String,
        offset: Int,
        limit: Int
    ): Result<List<Client>> {
        val normalizedQuery = query.trim()
        val cacheKey = SearchPageCacheKey(agentId, agencyId, normalizedQuery, offset, limit)
        return pageCacheMutex.withLock {
            val nowMs = Clock.System.now().toEpochMilliseconds()
            pageCache[cacheKey]
                ?.takeIf { nowMs - it.savedAtMs <= PAGE_CACHE_TTL_MS }
                ?.let { return@withLock Result.success(it.value) }

            val result = runCatching {
                HavnLogger.debug("Clients", "Loading contacts for agent $agentId")
                val rows = client.postgrest.rpc(
                    function = CLIENT_SEARCH_RPC,
                    parameters = SearchPageParams(
                        agencyId = agencyId,
                        query = normalizedQuery.takeIf { it.isNotBlank() },
                        limit = limit,
                        offset = offset
                    )
                )
                    .decodeList<ClientRow>()
                val openingsByClient = openingSummariesForClients(rows.map { it.id })
                rows.map { row -> row.toDomain(openings = openingsByClient[row.id].orEmpty()) }
            }.onFailure {
                HavnLogger.error("Clients", "Failed to load contacts for $agentId", it)
            }
            result.onSuccess { pageCache[cacheKey] = ClientPageCacheEntry(nowMs, it) }
            result
        }
    }

    override suspend fun clientDetail(agentId: String, clientId: String): Result<Client> {
        return runCatching {
            slowSqlDetail("clientDetail(contact_id=$clientId)") {
                HavnLogger.debug("Clients", "Loading contact detail $clientId")
                val row = slowSqlDetail("clientDetail.row(contact_id=$clientId)") {
                    client.from(CLIENTS)
                        .select {
                            filter {
                                eq("id", clientId)
                            }
                            limit(1)
                        }
                        .decodeSingle<ClientRow>()
                }
                val openings = slowSqlDetail("clientDetail.openings(contact_id=$clientId)") {
                    openingsForClient(agentId, clientId)
                }
                val inquiries = slowSqlDetail("clientDetail.inquiries(contact_id=$clientId)") {
                    inquiriesForClient(clientId)
                }
                row.toDomain(openings = openings, inquiries = inquiries)
            }
        }.onFailure {
            HavnLogger.error("Clients", "Failed to load contact $clientId", it)
        }
    }

    private suspend fun <T> slowSqlDetail(label: String, block: suspend () -> T): T {
        val startedAtMs = Clock.System.now().toEpochMilliseconds()
        return try {
            block()
        } finally {
            val elapsedMs = Clock.System.now().toEpochMilliseconds() - startedAtMs
            if (elapsedMs > SLOW_DETAIL_SQL_WARNING_THRESHOLD_MS) {
                HavnLogger.warning("SupabaseSQL", "Slow SQL detail $label took ${elapsedMs}ms")
            }
        }
    }

    private suspend fun openingsForClient(agentId: String, clientId: String): List<ClientOpening> {
        return client.postgrest.rpc(
            function = CLIENT_DETAIL_OPENINGS_RPC,
            parameters = ClientOpeningSummariesParams(
                contactIds = listOf(clientId),
                perContactLimit = CLIENT_DETAIL_OPENING_LIMIT
            )
        )
            .decodeList<ClientOpeningSummaryRow>()
            .mapNotNull { it.toDomain() }
    }

    private suspend fun inquiriesForClient(clientId: String): List<ClientInquiry> {
        val leads = client.from(LEADS)
            .select(Columns.list("id", "property_id", "opening_id", "temperature", "created_at", "updated_at", "desired_location", "location_radius_km", "budget_min", "budget_max", "purchase_timeline", "desired_property_type", "notes", "source")) {
                filter {
                    eq("contact_id", clientId)
                }
                order("created_at", Order.DESCENDING)
            }
            .decodeList<ClientInquiryLeadRow>()
            .filter { it.openingId.isNullOrBlank() }
            .take(CLIENT_DETAIL_INQUIRY_LIMIT)
        val properties = propertiesForClientInquiries(leads.mapNotNull { it.propertyId }.distinct())
        return leads.map { lead ->
            val property = properties[lead.propertyId]
            ClientInquiry(
                leadId = lead.id,
                propertyId = lead.propertyId,
                propertyTitle = property?.title ?: lead.desiredLocation.orEmpty(),
                propertyAddress = property?.address ?: listOfNotNull(lead.locationRadiusKm?.let { "$it km radius" }, lead.desiredPropertyType).joinToString(" · "),
                temperature = lead.temperature.orEmpty(),
                createdAt = lead.createdAt ?: lead.updatedAt,
                desiredLocation = lead.desiredLocation,
                locationRadiusKm = lead.locationRadiusKm,
                budgetMin = lead.budgetMin,
                budgetMax = lead.budgetMax,
                purchaseTimeline = lead.purchaseTimeline,
                desiredPropertyType = lead.desiredPropertyType,
                notes = lead.notes,
                source = lead.source
            )
        }
    }

    private suspend fun propertiesForClientInquiries(propertyIds: List<String>): Map<String, ClientInquiryPropertyRow> {
        if (propertyIds.isEmpty()) return emptyMap()
        return client.from(PROPERTIES)
            .select(Columns.list("id", "title", "address_line_1", "city", "region")) {
                filter { isIn("id", propertyIds) }
            }
            .decodeList<ClientInquiryPropertyRow>()
            .associateBy { it.id }
    }

    override suspend fun addClient(agentId: String, draft: ClientDraft, agencyId: String?): Result<Client> {
        return runCatching {
            HavnLogger.info("Clients", "Adding contact '${draft.fullName}' for agent $agentId")
            val row = client.from(CLIENTS)
                .insert(
                    ClientInsertRow(
                        agentId = agentId,
                        ownerAgentId = agentId,
                        agencyId = agencyId,
                        firstName = draft.firstName.trim(),
                        lastName = draft.lastName.trim(),
                        fullName = draft.fullName,
                        email = draft.email?.trim()?.takeIf { it.isNotBlank() },
                        phone = draft.phone?.trim()?.takeIf { it.isNotBlank() },
                        notes = draft.notes?.trim()?.takeIf { it.isNotBlank() }
                    )
                ) {
                    select()
                }
                .decodeSingle<ClientRow>()

            clearPageCache()
            row.toDomain()
        }.onFailure {
            HavnLogger.error("Clients", "Failed to add contact '${draft.fullName}'", it)
        }
    }

    override suspend fun updateClient(agentId: String, clientId: String, draft: ClientDraft): Result<Client> {
        return runCatching {
            HavnLogger.info("Clients", "Updating contact $clientId")
            client.from(CLIENTS).update(
                ClientUpdateRow(
                    firstName = draft.firstName.trim(),
                    lastName = draft.lastName.trim(),
                    fullName = draft.fullName,
                    email = draft.email?.trim()?.takeIf { it.isNotBlank() },
                    phone = draft.phone?.trim()?.takeIf { it.isNotBlank() },
                    notes = draft.notes?.trim()?.takeIf { it.isNotBlank() }
                )
            ) {
                filter {
                    eq("id", clientId)
                }
                select()
            }.decodeSingle<ClientRow>()

            clearPageCache()
            clientDetail(agentId, clientId).getOrThrow()
        }.onFailure {
            HavnLogger.error("Clients", "Failed to update contact $clientId", it)
        }
    }

    override suspend fun deleteClient(agentId: String, clientId: String): Result<Unit> {
        return runCatching {
            HavnLogger.warning("Clients", "Deleting contact $clientId")
            client.from(CLIENTS).delete {
                filter {
                    eq("id", clientId)
                }
            }
            clearPageCache()
            Unit
        }.onFailure {
            HavnLogger.error("Clients", "Failed to delete contact $clientId", it)
        }
    }

    override suspend fun updateClientNotes(agentId: String, clientId: String, notes: String): Result<Client> {
        return runCatching {
            HavnLogger.info("Clients", "Updating comments for contact $clientId")
            client.from(CLIENTS).update(ClientNotesUpdateRow(notes = notes.trim().takeIf { it.isNotBlank() })) {
                filter {
                    eq("id", clientId)
                }
                select()
            }.decodeSingle<ClientRow>()

            clearPageCache()
            clientDetail(agentId, clientId).getOrThrow()
        }.onFailure {
            HavnLogger.error("Clients", "Failed to update comments for contact $clientId", it)
        }
    }

    override suspend fun updateClientPhoto(
        agentId: String,
        clientId: String,
        photo: HavnPickedPhoto
    ): Result<Client> {
        return runCatching {
            HavnLogger.info("Clients", "Updating photo for contact $clientId")
            require(photo.bytes.isNotEmpty()) { "Choose a non-empty contact photo." }
            val existing = client.from(CLIENTS)
                .select(Columns.list("id", "avatar_storage_path", "avatar_thumbnail_storage_path")) {
                    filter { eq("id", clientId) }
                    limit(1)
                }
                .decodeSingleOrNull<ClientPhotoExistingRow>()
                ?: error("This contact was not saved yet. Please save it first, then add the photo.")
            val contentType = photo.contentType.ifBlank { "image/jpeg" }
            val safeName = photo.fileName.sanitizeFileName()
            val accessToken = client.auth.currentAccessTokenOrNull()
                ?: error("Sign in again before uploading a contact photo.")
            val thumbnailDataUrl = photo.listThumbnailDataUrl ?: photo.thumbnailDataUrl
            val thumbnailBytes = thumbnailDataUrl?.decodeHavnDataUrlBytes()
            val thumbnailContentType = thumbnailDataUrl?.havnDataUrlContentType(contentType) ?: contentType
            val request = ContactPhotoUploadRequest(
                contactId = existing.id,
                fileName = safeName,
                contentType = contentType,
                imageBase64 = photo.bytes.havnBase64(),
                thumbnailBase64 = thumbnailBytes?.takeIf { it.isNotEmpty() }?.havnBase64(),
                thumbnailContentType = thumbnailContentType
            )
            val httpResponse = httpClient.post("${HavnSupabaseConfig.url}/functions/v1/upload-contact-photo") {
                contentType(ContentType.Application.Json)
                header(HttpHeaders.Authorization, "Bearer $accessToken")
                header("apikey", HavnSupabaseConfig.anonKey)
                setBody(json.encodeToString(ContactPhotoUploadRequest.serializer(), request))
            }
            val response = httpResponse.body<String>()
            if (!httpResponse.status.isSuccess()) {
                val error = runCatching {
                    json.decodeFromString(ContactPhotoUploadErrorResponse.serializer(), response).error
                }.getOrNull()
                error(error ?: "The contact photo could not be uploaded.")
            } else {
                runCatching {
                    json.decodeFromString(ContactPhotoUploadResponse.serializer(), response)
                }.onSuccess { payload ->
                    HavnLogger.info(
                        "Clients",
                        "Uploaded contact photo for $clientId to ${payload.avatarStoragePath}"
                    )
                }
            }

            clearPageCache()
            clientDetail(agentId, clientId).getOrThrow()
        }.onFailure {
            HavnLogger.error("Clients", "Failed to update photo for contact $clientId", it)
        }
    }

    override suspend fun deleteClientPhoto(agentId: String, clientId: String): Result<Client> {
        return runCatching {
            HavnLogger.info("Clients", "Deleting photo for contact $clientId")
            val existing = client.from(CLIENTS)
                .select(Columns.list("id", "avatar_storage_path", "avatar_thumbnail_storage_path")) {
                    filter { eq("id", clientId) }
                    limit(1)
                }
                .decodeSingleOrNull<ClientPhotoExistingRow>()
                ?: error("This contact was not saved yet.")

            client.from(CLIENTS).update(ClientPhotoClearRow()) {
                filter { eq("id", clientId) }
                select()
            }.decodeSingle<ClientRow>()

            listOfNotNull(existing.avatarStoragePath, existing.avatarThumbnailStoragePath)
                .distinct()
                .forEach { path -> runCatching { client.storage.from(HavnMediaBucket.ContactPhotos).delete(path) } }

            clearPageCache()
            clientDetail(agentId, clientId).getOrThrow()
        }.onFailure {
            HavnLogger.error("Clients", "Failed to delete photo for contact $clientId", it)
        }
    }

    override suspend fun sendQrCode(agentId: String, clientId: String): Result<ClientQrDeliveryAssets> {
        return runCatching {
            val target = clientDetail(agentId, clientId).getOrThrow()
            val basePath = "$agentId/contacts/$clientId"
            val qrPath = "$basePath/qr-code.png"
            val passPath = "$basePath/wallet-pass.pkpass"
            client.storage.from(HavnMediaBucket.ContactPasses).upload(
                path = qrPath,
                data = HavnQrShareFiles.qrPngBytes(target.qrPayload)
            ) {
                upsert = true
                contentType = ContentType.Image.PNG
            }
            client.storage.from(HavnMediaBucket.ContactPasses).upload(
                path = passPath,
                data = HavnQrShareFiles.pkpassBytes(
                    payload = target.qrPayload,
                    title = target.fullName,
                    serial = target.walletPassKey
                )
            ) {
                upsert = true
                contentType = ContentType.parse("application/vnd.apple.pkpass")
            }
            client.from(CLIENTS).update(
                ClientQrAssetsUpdateRow(
                    qrCodeUrl = qrPath,
                    qrCodeStoragePath = qrPath,
                    walletPassUrl = passPath,
                    walletPassStoragePath = passPath
                )
            ) {
                filter { eq("id", clientId) }
            }
            clearPageCache()
            val qrUrl = havnPublicStorageUrl(HavnMediaBucket.ContactPasses, qrPath)
            val passUrl = havnPublicStorageUrl(HavnMediaBucket.ContactPasses, passPath)
            HavnLogger.info("Clients", "Prepared QR delivery for ${target.fullName} (${target.contactLine})")
            ClientQrDeliveryAssets(
                client = target.copy(
                    qrCodeUrl = qrUrl,
                    qrCodeStoragePath = qrPath,
                    walletPassUrl = passUrl,
                    walletPassStoragePath = passPath
                ),
                qrCodeUrl = qrUrl,
                walletPassUrl = passUrl
            )
        }.onFailure {
            HavnLogger.error("Clients", "Failed to prepare QR delivery for contact $clientId", it)
        }
    }

    private suspend fun clearPageCache() {
        pageCacheMutex.withLock { pageCache.clear() }
    }

    private suspend fun openingSummariesForClients(clientIds: List<String>): Map<String, List<ClientOpening>> {
        if (clientIds.isEmpty()) return emptyMap()
        return client.postgrest.rpc(
            function = CLIENT_OPENING_SUMMARIES_RPC,
            parameters = ClientOpeningSummariesParams(
                contactIds = clientIds,
                perContactLimit = CLIENT_OPENING_SUMMARY_LIMIT_PER_CONTACT
            )
        )
            .decodeList<ClientOpeningSummaryRow>()
            .mapNotNull { row ->
                val clientId = row.clientId?.takeIf { it.isNotBlank() } ?: return@mapNotNull null
                val opening = row.toDomain() ?: return@mapNotNull null
                clientId to opening
            }
            .groupBy(
                keySelector = { it.first },
                valueTransform = { it.second }
            )
            .mapValues { (_, openings) -> openings.take(CLIENT_OPENING_SUMMARY_LIMIT_PER_CONTACT) }
    }

    private companion object {
        const val CLIENT_PAGE_SIZE = 20
        const val CLIENT_OPENING_SUMMARY_LIMIT_PER_CONTACT = 2
        const val CLIENT_DETAIL_OPENING_LIMIT = 100
        const val CLIENT_DETAIL_INQUIRY_LIMIT = 50
        const val PAGE_CACHE_TTL_MS = 15_000L
        const val SLOW_DETAIL_SQL_WARNING_THRESHOLD_MS = 1_000L
        const val CLIENT_OPENING_SUMMARIES_RPC = "contact_opening_summaries_for_current_user"
        const val CLIENT_DETAIL_OPENINGS_RPC = "contact_detail_openings_for_current_user"
        const val CLIENTS = "contacts"
        const val LEADS = "leads"
        const val OPEN_HOUSES = "open_houses"
        const val PROPERTIES = "properties"
        const val PHOTOS = "property_photos"
        const val CLIENT_SEARCH_RPC = "search_contacts_for_current_user"
        val CLIENT_LIST_COLUMNS = Columns.list(
            "id",
            "agent_id",
            "first_name",
            "last_name",
            "full_name",
            "email",
            "phone",
            "qr_token",
            "wallet_pass_key",
            "pass_identity_id",
            "notes",
            "avatar_url",
            "avatar_storage_path",
            "avatar_content_type",
            "created_at",
            "updated_at"
        )
    }
}

private fun String.sanitizeFileName(): String =
    lowercase()
        .replace(Regex("[^a-z0-9._-]+"), "-")
        .trim('-', '.', '_')
        .ifBlank { "photo.jpg" }

@OptIn(ExperimentalEncodingApi::class)
private fun ByteArray.havnBase64(): String = Base64.encode(this)

@Serializable
private data class ClientRow(
    val id: String,
    @SerialName("agent_id") val agentId: String,
    @SerialName("first_name") val firstName: String? = null,
    @SerialName("last_name") val lastName: String? = null,
    @SerialName("full_name") val fullName: String,
    val email: String? = null,
    val phone: String? = null,
    @SerialName("qr_token") val qrToken: String,
    @SerialName("wallet_pass_key") val walletPassKey: String,
    @SerialName("pass_identity_id") val passIdentityId: String? = null,
    val notes: String? = null,
    @SerialName("avatar_url") val avatarDataUrl: String? = null,
    @SerialName("avatar_storage_path") val avatarStoragePath: String? = null,
    @SerialName("avatar_thumbnail_url") val avatarThumbnailUrl: String? = null,
    @SerialName("avatar_thumbnail_storage_path") val avatarThumbnailStoragePath: String? = null,
    @SerialName("qr_code_url") val qrCodeUrl: String? = null,
    @SerialName("qr_code_storage_path") val qrCodeStoragePath: String? = null,
    @SerialName("wallet_pass_url") val walletPassUrl: String? = null,
    @SerialName("wallet_pass_storage_path") val walletPassStoragePath: String? = null,
    @SerialName("avatar_content_type") val avatarContentType: String? = null,
    @SerialName("created_at") val createdAt: String? = null,
    @SerialName("updated_at") val updatedAt: String? = null
) {
    fun toDomain(openings: List<ClientOpening> = emptyList(), inquiries: List<ClientInquiry> = emptyList()): Client = Client(
        id = id,
        agentId = agentId,
        firstName = firstName,
        lastName = lastName,
        fullName = fullName,
        email = email,
        phone = phone,
        qrToken = qrToken,
        walletPassKey = walletPassKey,
        passIdentityId = passIdentityId,
        notes = notes,
        avatarDataUrl = havnPublicStorageSource(HavnMediaBucket.ContactPhotos, avatarStoragePath ?: avatarDataUrl),
        avatarStoragePath = avatarStoragePath,
        avatarThumbnailDataUrl = havnPreviewStorageSource(
            HavnMediaBucket.ContactPhotos,
            avatarThumbnailStoragePath ?: avatarThumbnailUrl,
            width = 96
        ),
        avatarThumbnailStoragePath = avatarThumbnailStoragePath,
        qrCodeUrl = havnPublicStorageSource(HavnMediaBucket.ContactPasses, qrCodeStoragePath ?: qrCodeUrl),
        qrCodeStoragePath = qrCodeStoragePath,
        walletPassUrl = havnPublicStorageSource(HavnMediaBucket.ContactPasses, walletPassStoragePath ?: walletPassUrl),
        walletPassStoragePath = walletPassStoragePath,
        avatarContentType = avatarContentType,
        createdAt = createdAt,
        updatedAt = updatedAt,
        openings = openings,
        inquiries = inquiries
    )
}

@Serializable
private data class ClientInsertRow(
    @SerialName("agent_id") val agentId: String,
    @SerialName("owner_agent_id") val ownerAgentId: String,
    @SerialName("agency_id") val agencyId: String? = null,
    @SerialName("first_name") val firstName: String,
    @SerialName("last_name") val lastName: String,
    @SerialName("full_name") val fullName: String,
    val email: String? = null,
    val phone: String? = null,
    val notes: String? = null
)

@Serializable
private data class ClientUpdateRow(
    @SerialName("first_name") val firstName: String,
    @SerialName("last_name") val lastName: String,
    @SerialName("full_name") val fullName: String,
    val email: String? = null,
    val phone: String? = null,
    val notes: String? = null
)

@Serializable
private data class ClientNotesUpdateRow(
    val notes: String? = null
)

@Serializable
private data class ClientPhotoExistingRow(
    val id: String,
    @SerialName("avatar_storage_path") val avatarStoragePath: String? = null,
    @SerialName("avatar_thumbnail_storage_path") val avatarThumbnailStoragePath: String? = null
)

@Serializable
private data class ClientPhotoClearRow(
    @SerialName("avatar_url") val avatarDataUrl: String? = null,
    @SerialName("avatar_storage_path") val avatarStoragePath: String? = null,
    @SerialName("avatar_thumbnail_url") val avatarThumbnailUrl: String? = null,
    @SerialName("avatar_thumbnail_storage_path") val avatarThumbnailStoragePath: String? = null,
    @SerialName("avatar_content_type") val avatarContentType: String? = null
)

@Serializable
private data class ContactPhotoUploadRequest(
    @SerialName("contact_id") val contactId: String,
    @SerialName("file_name") val fileName: String,
    @SerialName("content_type") val contentType: String,
    @SerialName("image_base64") val imageBase64: String,
    @SerialName("thumbnail_base64") val thumbnailBase64: String? = null,
    @SerialName("thumbnail_content_type") val thumbnailContentType: String? = null
)

@Serializable
private data class ContactPhotoUploadResponse(
    @SerialName("avatar_storage_path") val avatarStoragePath: String,
    @SerialName("avatar_thumbnail_storage_path") val avatarThumbnailStoragePath: String,
    @SerialName("avatar_content_type") val avatarContentType: String
)

@Serializable
private data class ContactPhotoUploadErrorResponse(
    val error: String? = null
)

@Serializable
private data class ClientQrAssetsUpdateRow(
    @SerialName("qr_code_url") val qrCodeUrl: String,
    @SerialName("qr_code_storage_path") val qrCodeStoragePath: String,
    @SerialName("wallet_pass_url") val walletPassUrl: String,
    @SerialName("wallet_pass_storage_path") val walletPassStoragePath: String
)

@Serializable
private data class SearchPageParams(
    @SerialName("p_agency_id") val agencyId: String?,
    @SerialName("p_query") val query: String?,
    @SerialName("p_limit") val limit: Int,
    @SerialName("p_offset") val offset: Int
)

private data class SearchPageCacheKey(
    val agentId: String,
    val agencyId: String?,
    val query: String,
    val offset: Int,
    val limit: Int
)

private data class ClientPageCacheEntry(
    val savedAtMs: Long,
    val value: List<Client>
)

@Serializable
private data class ClientOpeningSummariesParams(
    @SerialName("p_contact_ids") val contactIds: List<String>,
    @SerialName("p_per_contact_limit") val perContactLimit: Int
)

@Serializable
private data class ClientOpeningSummaryRow(
    val id: String,
    @SerialName("contact_id") val clientId: String? = null,
    @SerialName("property_id") val propertyId: String? = null,
    @SerialName("opening_id") val openingId: String? = null,
    val temperature: String,
    val status: String,
    @SerialName("opening_title") val openingTitle: String? = null,
    @SerialName("starts_at") val startsAt: String? = null,
    @SerialName("property_title") val propertyTitle: String? = null,
    @SerialName("property_address_line_1") val propertyAddressLine1: String? = null,
    @SerialName("property_city") val propertyCity: String? = null,
    @SerialName("property_region") val propertyRegion: String? = null,
    @SerialName("property_hero_storage_path") val propertyHeroStoragePath: String? = null,
    @SerialName("property_hero_thumbnail_data_url") val propertyHeroThumbnailDataUrl: String? = null,
    @SerialName("property_hero_thumbnail_width") val propertyHeroThumbnailWidth: Int? = null
) {
    val propertyAddress: String
        get() = listOfNotNull(
            propertyAddressLine1?.takeIf { it.isNotBlank() },
            propertyCity?.takeIf { it.isNotBlank() },
            propertyRegion?.takeIf { it.isNotBlank() }
        ).joinToString(", ")

    fun toDomain(): ClientOpening? {
        val resolvedOpeningId = openingId?.takeIf { it.isNotBlank() } ?: return null
        val resolvedPropertyId = propertyId?.takeIf { it.isNotBlank() } ?: return null
        return ClientOpening(
        leadId = id,
        openingId = resolvedOpeningId,
        propertyId = resolvedPropertyId,
        propertyTitle = propertyTitle.orEmpty(),
        propertyAddress = propertyAddress,
        propertyPhotoDataUrl = havnDisplayPhotoSource(
            storagePath = propertyHeroStoragePath,
            thumbnailDataUrl = propertyHeroThumbnailDataUrl,
            thumbnailWidth = propertyHeroThumbnailWidth
        ),
        openingTitle = openingTitle,
        startsAt = startsAt.orEmpty(),
        status = status,
        temperature = temperature
    )
    }
}

@Serializable
private data class ClientInquiryLeadRow(
    val id: String,
    @SerialName("property_id") val propertyId: String? = null,
    @SerialName("opening_id") val openingId: String? = null,
    val temperature: String? = null,
    @SerialName("created_at") val createdAt: String? = null,
    @SerialName("updated_at") val updatedAt: String? = null,
    @SerialName("desired_location") val desiredLocation: String? = null,
    @SerialName("location_radius_km") val locationRadiusKm: Int? = null,
    @SerialName("budget_min") val budgetMin: Long? = null,
    @SerialName("budget_max") val budgetMax: Long? = null,
    @SerialName("purchase_timeline") val purchaseTimeline: String? = null,
    @SerialName("desired_property_type") val desiredPropertyType: String? = null
    ,val notes: String? = null
    ,val source: String? = null
)

@Serializable
private data class ClientInquiryPropertyRow(
    val id: String,
    val title: String? = null,
    @SerialName("address_line_1") val addressLine1: String? = null,
    val city: String? = null,
    val region: String? = null
) {
    val address: String
        get() = listOfNotNull(
            addressLine1?.takeIf { it.isNotBlank() },
            city?.takeIf { it.isNotBlank() },
            region?.takeIf { it.isNotBlank() }
        ).joinToString(", ")
}
